'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ZoomIn, ZoomOut, MoveHorizontal, MoveVertical } from 'lucide-react'

// scale is a multiplier on the viewport WIDTH (see render). Fit-height on a
// very tall full-page capture needs the image narrower than the viewport, i.e.
// scale < 1, so the floor is well below 1.
const MIN_SCALE = 0.05
const MAX_SCALE = 8

export function ScreenshotLightbox({ src, label, description, onClose }: { src: string; label: string; description?: string | null; onClose: () => void }) {
  const [scale, setScale] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  // Viewport is a stable, unscaled frame — only the <img> inside it is
  // transformed — so its bounding rect is a reliable reference for
  // zoom-toward-cursor math regardless of current scale/pan.
  const viewportRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)

  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }) }, [])

  // Fit-width: image spans the viewport width (scale 1); a tall page then
  // scrolls vertically. Fit-height: shrink the width multiplier until the
  // whole image height fits the viewport (the old see-it-all view).
  const fitWidth = useCallback(() => {
    setScale(1)
    const el = viewportRef.current
    if (el) { el.scrollTop = 0; el.scrollLeft = 0 }
  }, [])

  const fitHeight = useCallback(() => {
    const el = viewportRef.current
    const img = imgRef.current
    if (!el || !img || !img.naturalWidth || !img.naturalHeight) return
    const aspect = img.naturalHeight / img.naturalWidth
    // rendered height = (scale * viewportWidth) * aspect ; solve for == viewportHeight
    const s = el.clientHeight / (el.clientWidth * aspect)
    setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, s)))
    el.scrollTop = 0; el.scrollLeft = 0
  }, [])

  // The image is laid out at `scale`× the viewport WIDTH and the viewport
  // scrolls (see render), so full-page captures — which are far taller than
  // wide — are readable at full width and you scroll down to the bottom rather
  // than squinting at a fit-to-height sliver. Zoom just changes that width
  // multiplier; the cursor position is intentionally ignored because native
  // scrolling, not a transform, positions the view now.
  const zoomAt = useCallback((_clientX: number, _clientY: number, factor: number) => {
    setScale((prev) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor)))
  }, [])

  // Plain wheel scrolls the (tall) image natively; only Ctrl/⌘+wheel zooms.
  // React's onWheel is passive, so a native non-passive listener is required
  // to preventDefault on the zoom gesture.
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.2 : 1 / 1.2)
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [zoomAt])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === '+' || e.key === '=') zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1.3)
      else if (e.key === '-') zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1 / 1.3)
      else if (e.key === '0') fitWidth()
      else if (e.key === '9') fitHeight()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, zoomAt, fitWidth, fitHeight])

  // Drag-to-pan by moving the viewport's native scroll offset (origX/origY
  // now hold the scroll position at grab time), so grabbing works the same
  // whether the overflow is vertical (tall page) or horizontal (zoomed in).
  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current
      const el = viewportRef.current
      if (!d || !el) return
      el.scrollLeft = d.origX - (e.clientX - d.startX)
      el.scrollTop = d.origY - (e.clientY - d.startY)
    }
    const onUp = () => { setDragging(false); dragRef.current = null }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragging])

  const onMouseDown = (e: React.MouseEvent) => {
    const el = viewportRef.current
    if (!el) return
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: el.scrollLeft, origY: el.scrollTop }
    setDragging(true)
  }

  const onDoubleClick = (e: React.MouseEvent) => {
    if (scale > MIN_SCALE) reset()
    else zoomAt(e.clientX, e.clientY, 2.5)
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="relative flex w-[90vw] max-w-[90vw] flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex w-full items-center justify-between px-1">
          <span className="truncate font-mono text-[10px] uppercase tracking-widest text-white/60">{label}</span>
          <div className="flex shrink-0 items-center gap-1">
            <button onClick={() => zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1 / 1.3)} className="rounded p-1 text-white/60 hover:text-white" title="Zoom out (-)">
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="w-10 text-center font-mono text-[10px] text-white/60">{Math.round(scale * 100)}%</span>
            <button onClick={() => zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1.3)} className="rounded p-1 text-white/60 hover:text-white" title="Zoom in (+)">
              <ZoomIn className="h-4 w-4" />
            </button>
            <button onClick={fitWidth} className="rounded p-1 text-white/60 hover:text-white" title="Fit width (0)">
              <MoveHorizontal className="h-4 w-4" />
            </button>
            <button onClick={fitHeight} className="rounded p-1 text-white/60 hover:text-white" title="Fit height (9)">
              <MoveVertical className="h-4 w-4" />
            </button>
            <button onClick={onClose} className="ml-1 text-lg leading-none text-white/60 hover:text-white" title="Close (Esc)">✕</button>
          </div>
        </div>
        <div
          ref={viewportRef}
          className="relative h-[80vh] w-full overflow-auto"
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
          style={{ cursor: dragging ? 'grabbing' : 'grab' }}
        >
          <img
            ref={imgRef}
            src={src}
            alt={label}
            draggable={false}
            className="mx-auto block h-auto select-none rounded shadow-2xl"
            style={{ width: `${scale * 100}%`, maxWidth: 'none' }}
          />
        </div>
        {description && (
          <p className="w-full rounded bg-overlay px-3 py-2 text-left text-xs leading-relaxed text-white/80">{description}</p>
        )}
      </div>
    </div>
  )
}

export function ImageTile({
  label,
  src,
  caption,
  description,
  fallback,
  onExpand,
}: {
  label: string
  src: string | null
  caption?: string
  description?: string | null
  fallback: React.ReactNode
  onExpand: () => void
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-[#090f14]">
      {src ? (
        <button onClick={onExpand} className="group relative block h-full w-full overflow-hidden">
          <div className="flex aspect-[4/5] items-start justify-center bg-black">
            <img src={src} alt={label} className="h-full w-full object-cover object-top" />
          </div>
          <span className="absolute left-2 right-2 top-2 flex items-center gap-2 rounded bg-black/70 px-2 py-1 backdrop-blur-sm">
            <span className="truncate font-mono text-[9px] uppercase tracking-widest text-white">{label}</span>
          </span>
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
            <span className="opacity-0 group-hover:opacity-100 font-mono text-[8px] uppercase text-white bg-overlay rounded px-1.5 py-0.5 transition-opacity">expand</span>
          </span>
        </button>
      ) : (
        <div className="flex aspect-[4/5] items-center justify-center bg-[radial-gradient(circle_at_35%_25%,rgba(239,68,68,0.12),transparent_28%),radial-gradient(circle_at_65%_70%,rgba(14,165,233,0.12),transparent_28%)] px-3 text-center font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
          {fallback}
        </div>
      )}
      <div className="border-t border-border px-3 py-2 flex items-center justify-between gap-2">
        <span className="font-mono text-xs text-muted-foreground">{label}</span>
        {caption && <span className="font-mono text-[9px] text-muted-foreground">{caption}</span>}
      </div>
      {description && (
        <p className="border-t border-border px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">{description}</p>
      )}
    </div>
  )
}

export function extractScreenshotImages(decData: any) {
  const scenarios = decData?.data?.buffer?.scenarios
  if (!scenarios) return null
  const desktop = scenarios?.desktop?.screenshots?.[0]?.data
  const mobile = scenarios?.mobile?.screenshots?.[0]?.data
  return {
    desktop: desktop ? `data:image/png;base64,${desktop}` : null,
    mobile: mobile ? `data:image/png;base64,${mobile}` : null,
  }
}
